import type { NotificationItem, NotificationSeverity } from "@steelyard/theme";
import { identity, unwrap } from "../api";
import type { PagedResult } from "../../components/table/data-table";

/**
 * Identity's back-office inbox (`NotificationsController.cs`, backend
 * `specs/002-identity-authentication/contracts/notifications.md`). Every service raises notices;
 * Identity filters them by the caller's live permissions, so nothing here decides who sees what.
 *
 * Runtime-parsed like `audit.ts`: nothing is rendered from a partial row (constitution VI).
 */

export type NotificationPage = PagedResult<NotificationItem> & { unreadCount: number };

const SEVERITIES: readonly NotificationSeverity[] = ["info", "warning", "critical"];

function parseItem(raw: Record<string, unknown>): NotificationItem {
  const { id, kind, title, body, severity, occurredAt, read, link } = raw;

  if (
    typeof id !== "string" ||
    typeof title !== "string" ||
    typeof body !== "string" ||
    typeof occurredAt !== "string" ||
    typeof read !== "boolean" ||
    !SEVERITIES.includes(severity as NotificationSeverity)
  ) {
    throw new Error(
      "GET /api/identity/me/notifications answered with a row missing id, title, body, severity, " +
        "occurredAt or read. Nothing is rendered from a partial row.",
    );
  }

  return {
    id,
    // Only the icon depends on it, so a missing kind falls back to the severity's icon rather than
    // refusing the row.
    kind: typeof kind === "string" ? kind : "",
    title,
    body,
    severity: severity as NotificationSeverity,
    occurredAt,
    read,
    // Identity only stores a single-slash back-office path; anything else is dropped, not followed.
    link: typeof link === "string" && link.startsWith("/") && !link.startsWith("//") ? link : null,
  };
}

export async function listNotifications(params: {
  page: number;
  pageSize: number;
  unreadOnly?: boolean;
}): Promise<NotificationPage> {
  const { page, pageSize, unreadOnly } = params;
  const raw = ((await unwrap(
    (await identity()).GET("/api/identity/me/notifications", {
      params: { query: { page, pageSize, unreadOnly } },
    }),
  )) ?? {}) as Record<string, unknown>;

  const totalCount = Number(raw.totalCount);
  const unreadCount = Number(raw.unreadCount);

  if (!Array.isArray(raw.items) || !Number.isFinite(totalCount) || !Number.isFinite(unreadCount)) {
    throw new Error("GET /api/identity/me/notifications answered without items, totalCount and unreadCount.");
  }

  return {
    items: raw.items.map((item) => parseItem((item ?? {}) as Record<string, unknown>)),
    page,
    pageSize,
    totalCount,
    unreadCount,
  };
}

export async function markNotificationRead(id: string): Promise<void> {
  await unwrap(
    (await identity()).POST("/api/identity/me/notifications/{id}/read", { params: { path: { id } } }),
  );
}

export async function markAllNotificationsRead(): Promise<void> {
  await unwrap((await identity()).POST("/api/identity/me/notifications/read-all"));
}
